import LaunchNotify, { useSalesOpen } from "./LaunchNotify";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadIcon, KeyIcon, ShieldCheckIcon, UserPlusIcon, RobotIcon } from "@phosphor-icons/react";
import { storeApi } from "@/lib/storeApi";
import { usePageView } from "@/lib/usePageView";
import HubFooter from "./HubFooter";
import StoreTopbar from "./StoreTopbar";
import FleetStats from "./mt5/FleetStats";
import Catalog from "./mt5/Catalog";
import StressShowcase from "./mt5/StressShowcase";
import PassedSection from "./mt5/PassedSection";
import HowWeTest from "./mt5/HowWeTest";
import StoreFaq from "./StoreFaq";
import { RiskNotice, SoftwareModel } from "./RiskNotice";

export default function Mt5Landing() {
  const salesOpen = useSalesOpen();
  usePageView("/mt5");
  const [data, setData] = useState(null);
  useEffect(() => {
    storeApi.get("/store/mt5/catalog").then((r) => setData(r.data)).catch(() => setData({ symbols: [], showcase: [] }));
  }, []);

  return (
    <div data-testid="mt5-landing" className="relative min-h-screen bg-[#050914] text-[#F1F5F9] overflow-x-clip">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-10 -left-20 w-96 h-96 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="absolute top-64 right-0 w-[28rem] h-[28rem] rounded-full bg-yellow-400/10 blur-3xl" />
      </div>
      <StoreTopbar subtitle="Expert Advisors marketplace" />

      <section className="relative z-10 max-w-[1400px] mx-auto px-6 pt-16 pb-12">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#FBBF24]">MetaTrader 5 · Expert Advisors</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mt-4 max-w-4xl leading-[1.05]">
          AI-bred Expert Advisors.
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-yellow-300 bg-clip-text text-transparent">Every result on the table.</span>
        </h1>
        <p className="text-base md:text-lg text-[#94A3B8] mt-6 max-w-2xl leading-relaxed">
          Downloadable Expert Advisors for MetaTrader 5, developed and backtested by our automated research pipeline. Every listing
          shows its full month-by-month backtest, drawdowns, losing months, robustness checks and its result on unseen 2025+ data -
          all clearly labelled as simulated. You choose, install and run the software on your own MT5 account.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#bots" data-testid="mt5-hero-browse" className="mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg bg-[#FBBF24] text-[#050914] hover:scale-[1.03] transition-transform">
            Browse the bots
          </a>
          <Link to="/account/register" data-testid="mt5-hero-register" className="mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg border border-[#38BDF8] text-[#38BDF8] hover:bg-[#38BDF8]/10 transition-colors">
            Create a free account
          </Link>
          <a href="#how-we-test" data-testid="mt5-hero-how" className="mono text-[11px] uppercase tracking-widest px-5 py-3 text-[#94A3B8] hover:text-white">
            How we test →
          </a>
        </div>
        <RiskNotice testid="mt5-hero-risk" className="mt-8 max-w-4xl" />
      </section>

      {salesOpen === false && (
        <section className="relative z-10 max-w-[1400px] mx-auto px-6 pb-10" id="notify"><LaunchNotify testid="mt5-launch-notify" /></section>
      )}
      <FleetStats />
      <Catalog data={data} />
      <PassedSection data={data} />
      <StressShowcase data={data} />
      <HowWeTest />
      <SoftwareModel />

      <section className="relative z-10 max-w-[1400px] mx-auto px-6 pb-16">
        <div className="relative overflow-hidden border border-[#38BDF8]/40 rounded-xl p-8 md:p-10 grid md:grid-cols-[1.3fr_1fr] gap-8"
             style={{ background: "linear-gradient(135deg, rgba(30,58,138,0.35) 0%, rgba(202,138,4,0.10) 100%)" }}>
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-[#FBBF24]">How it works</div>
            <h2 className="text-3xl font-medium tracking-tight mt-3">Account, licence plan, choose your bots.</h2>
            <p className="text-[#CBD5E1] mt-4 leading-relaxed text-sm">
              Create an account, choose a monthly software-licence plan (1, 5 or all bots), then choose the bots you want. Download the
              protected .ex5 files and your licence key from your account page, install them in your own MT5 terminal and decide whether
              to switch them on. Cancel any time - the software stops opening trades when the plan ends.
            </p>
            <Link to="/account/register" data-testid="mt5-cta-register" className="mt-6 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg bg-[#FBBF24] text-[#050914] hover:scale-[1.03] transition-transform">
              <UserPlusIcon size={14} /> Create account
            </Link>
          </div>
          <ul className="space-y-4">
            {[
              [DownloadIcon, "Compiled .ex5 files", "Drag into MT5. You never receive or need the source code."],
              [KeyIcon, "Licence-key protected", "Each picked bot runs on up to 2 MT5 accounts: 1 demo + 1 live."],
              [ShieldCheckIcon, "You stay in control", "Runs on your own MT5 and broker. We never hold your funds or trade for you."],
              [RobotIcon, "Free to backtest", "Run any bot in your own MT5 strategy tester and check the backtests yourself."],
            ].map(([Icon, t, b]) => (
              <li key={t} className="flex items-start gap-3">
                <Icon size={18} weight="duotone" className="mt-0.5 text-[#FBBF24]" />
                <div><div className="text-sm">{t}</div><div className="text-xs text-[#94A3B8] mt-0.5">{b}</div></div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <StoreFaq accent="#38BDF8" />
      <HubFooter />
    </div>
  );
}
